import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { downloadFile, inr } from "@/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export default function Reports() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [pnl, aging, gst, daybook] = await Promise.all([
        api.get("/reports/pnl", { params: { month } }),
        api.get("/reports/aging"),
        api.get("/reports/gst", { params: { month } }),
        api.get("/reports/daybook"),
      ]);
      setReports({ pnl: pnl.data, aging: aging.data, gst: gst.data, daybook: daybook.data });
    } catch (error) { toast.error(error.response?.data?.detail || "Reports could not be loaded"); }
    finally { setBusy(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const exportReport = async (format) => {
    const extension = format === "xlsx" ? "xlsx" : "pdf";
    try { await downloadFile(`/reports/export.${extension}`, `business-report-${month}.${extension}`); }
    catch { toast.error(`Could not export ${extension.toUpperCase()} report`); }
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight">Reports</h1><p className="text-sm text-muted-foreground">Sales, collections, outstanding, profit and loss, and GST.</p></div>
        <div className="flex gap-2 items-center flex-wrap">
          <Input aria-label="Report month" type="month" value={month} onChange={(event) => setMonth(event.target.value)} className="w-40" />
          <Button variant="outline" data-testid="reports-refresh" onClick={load} disabled={busy}>{busy ? "Loading…" : "Refresh"}</Button>
          <Button variant="outline" data-testid="reports-export-xlsx" onClick={() => exportReport("xlsx")}>Export Excel</Button>
          <Button variant="outline" data-testid="reports-export-pdf" onClick={() => exportReport("pdf")}>Export PDF</Button>
        </div>
      </div>
      {!reports ? <div className="text-sm text-muted-foreground">{busy ? "Loading reports…" : "No report data available."}</div> : <>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {[["Revenue", reports.pnl.revenue], ["Collections today", reports.daybook.inflow], ["Outstanding", Object.values(reports.aging.buckets).reduce((sum, value) => sum + value, 0)], ["Gross profit", reports.pnl.gross_profit]].map(([label, value]) => (
            <Card key={label} className="p-4"><div className="text-xs uppercase text-muted-foreground">{label}</div><div className="mt-1 font-mono text-xl font-semibold">{inr(value)}</div></Card>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-4 space-y-3">
            <h2 className="font-heading font-semibold">Profit & Loss · {reports.pnl.month}</h2>
            <ReportRows rows={[["Revenue", reports.pnl.revenue], ["GST collected", reports.pnl.gst_collected], ["Expenses", reports.pnl.expenses], ["Gross profit", reports.pnl.gross_profit]]} />
          </Card>
          <Card className="p-4 space-y-3">
            <h2 className="font-heading font-semibold">GST Summary · {reports.gst.month}</h2>
            <ReportRows rows={[["Taxable value", reports.gst.totals.taxable], ["GST", reports.gst.totals.gst]]} />
            <div className="text-xs text-muted-foreground">{reports.gst.rows.length} invoices in this period</div>
          </Card>
          <Card className="p-4 space-y-3">
            <h2 className="font-heading font-semibold">Outstanding by age</h2>
            <ReportRows rows={Object.entries(reports.aging.buckets)} />
            <div className="text-xs text-muted-foreground">{reports.aging.rows.length} unpaid or partially paid invoices</div>
          </Card>
          <Card className="p-4 space-y-3">
            <h2 className="font-heading font-semibold">Daybook · {reports.daybook.date}</h2>
            <ReportRows rows={[["Receipts", reports.daybook.inflow], ["Paid out", reports.daybook.outflow], ["Net movement", reports.daybook.net]]} />
            <div className="text-xs text-muted-foreground">{reports.daybook.receipts.length} receipts · {reports.daybook.expenses.length} expenses</div>
          </Card>
        </div>
      </>}
    </div>
  );
}

function ReportRows({ rows }) {
  return <div className="divide-y">{rows.map(([label, value]) => <div key={label} className="flex justify-between gap-4 py-2 text-sm"><span className="text-muted-foreground">{label}</span><span className="font-mono">{inr(value)}</span></div>)}</div>;
}